<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  useId,
  toRef,
  watch,
  useSlots,
} from "vue"
import {
  computeLetterformCoords,
  buildPathD,
} from "@/composables/use-letterform"
import { useLetterformPull } from "@/composables/use-letterform-pull"
import {
  useResponsiveProp,
  type ResponsiveProp,
} from "@/composables/use-responsive-prop"
import type { LetterMasters } from "@/types/letterform"

const props = withDefaults(
  defineProps<{
    masters: LetterMasters
    stroke?: string
    strokeWidth?: number
    minGapScale?: number | ResponsiveProp<number>
    // Enable the ambient mouse-pull interaction (anchor pairs lean toward the
    // cursor — horizontal groups on X, vertical groups on Y).
    interactive?: boolean
    // Max horizontal travel per anchor, px. Defaults to ~8% of width.
    maxPull?: number
    // Proximity falloff radius, px. Defaults to ~25% of width.
    pullRadius?: number
    // Listen for pointer movement on `window` instead of this component's own
    // wrapper, hit-testing the cursor against the letter's own bounding box (the
    // "mask"). Use when an overlay sits on top of the letter and swallows its
    // pointer events — window-level listening can't be blocked, and because the
    // mask is per-component each letter reacts only over its own area.
    global?: boolean
  }>(),
  { stroke: "none", strokeWidth: 0, minGapScale: 1, interactive: false },
)

const slots = useSlots()
const hasDefaultSlot = computed(() => Boolean(slots.default))

const resolvedMinGapScale = useResponsiveProp(() => props.minGapScale)

const clipId = useId()
const wrapper = ref<HTMLElement | null>(null)
const width = ref(0)
const height = ref(0)

let ro: ResizeObserver | null = null

onMounted(() => {
  if (!wrapper.value) return
  ro = new ResizeObserver(entries => {
    const { width: w, height: h } = entries[0].contentRect
    width.value = w
    height.value = h
  })
  ro.observe(wrapper.value)
})

onUnmounted(() => {
  ro?.disconnect()
})

// Base interpolation (aspect-driven), unchanged in behaviour.
const base = computed(() =>
  computeLetterformCoords(
    props.masters,
    width.value,
    height.value,
    resolvedMinGapScale.value ?? 1,
  ),
)

const { coords, onPointerEnter, onPointerMove, onPointerLeave } =
  useLetterformPull({
    base,
    masters: toRef(props, "masters"),
    width,
    height,
    enabled: toRef(props, "interactive"),
    maxPull: toRef(props, "maxPull"),
    pullRadius: toRef(props, "pullRadius"),
  })

const d = computed(() => buildPathD(coords.value, base.value.commands))

function relativeX(e: PointerEvent): number {
  const rect = wrapper.value?.getBoundingClientRect()
  return rect ? e.clientX - rect.left : e.clientX
}

function relativeY(e: PointerEvent): number {
  const rect = wrapper.value?.getBoundingClientRect()
  return rect ? e.clientY - rect.top : e.clientY
}

// Default mode: pointer listeners are bound programmatically to this component's
// own wrapper (rather than in the template) so the binding can be swapped out for
// the window-level listeners used in `global` mode.
function handleEnter(e: PointerEvent) {
  if (!props.interactive) return
  onPointerEnter(relativeX(e), relativeY(e))
}
function handleMove(e: PointerEvent) {
  if (!props.interactive) return
  onPointerMove(relativeX(e), relativeY(e))
}
function handleLeave() {
  if (!props.interactive) return
  onPointerLeave()
}

const target = computed<HTMLElement | null>(() =>
  props.global ? null : wrapper.value,
)

function bind(el: HTMLElement) {
  el.addEventListener("pointerenter", handleEnter as EventListener)
  el.addEventListener("pointermove", handleMove as EventListener)
  el.addEventListener("pointerleave", handleLeave as EventListener)
}
function unbind(el: HTMLElement) {
  el.removeEventListener("pointerenter", handleEnter as EventListener)
  el.removeEventListener("pointermove", handleMove as EventListener)
  el.removeEventListener("pointerleave", handleLeave as EventListener)
}

// `target` resolves to null during setup (wrapper ref fills in post-mount), so
// the immediate watch re-fires once it's available, and again on any change. In
// `global` mode it stays null — the window listeners below take over instead.
watch(
  target,
  (el, prev) => {
    if (prev) unbind(prev)
    if (el) bind(el)
  },
  { immediate: true },
)

// Global mode: listen on `window` (which overlays can't block) and hit-test the
// cursor against this letter's own bounding box. Inside the box is the active
// "mask" — move; outside, once we've been inside, is a leave (spring back).
let wasInside = false
function handleWindowMove(e: PointerEvent) {
  if (!props.interactive) return
  const rect = wrapper.value?.getBoundingClientRect()
  if (!rect) return
  const inside =
    e.clientX >= rect.left &&
    e.clientX <= rect.right &&
    e.clientY >= rect.top &&
    e.clientY <= rect.bottom
  if (inside) {
    onPointerMove(e.clientX - rect.left, e.clientY - rect.top)
    wasInside = true
  } else if (wasInside) {
    onPointerLeave()
    wasInside = false
  }
}
// Pointer left the viewport entirely while still inside the mask — spring back.
function handleWindowLeave() {
  if (wasInside) {
    onPointerLeave()
    wasInside = false
  }
}

onMounted(() => {
  if (props.global) {
    window.addEventListener("pointermove", handleWindowMove)
    window.addEventListener("pointerleave", handleWindowLeave)
  }
})

onUnmounted(() => {
  if (target.value) unbind(target.value)
  window.removeEventListener("pointermove", handleWindowMove)
  window.removeEventListener("pointerleave", handleWindowLeave)
})
</script>

<template>
  <div ref="wrapper" class="w-full h-full">
    <svg
      :viewBox="`0 0 ${width} ${height}`"
      preserveAspectRatio="none"
      style="width: 100%; height: 100%; display: block"
    >
      <defs>
        <clipPath :id="clipId">
          <path :d="d" />
        </clipPath>
      </defs>
      <slot :width="width" :height="height" :clip-id="clipId" />
      <path
        :d="d"
        :fill="hasDefaultSlot ? 'none' : undefined"
        :stroke="stroke"
        :stroke-width="strokeWidth * 2"
        :clip-path="`url(#${clipId})`"
      />
    </svg>
  </div>
</template>
